import { useState, useCallback, useRef, useImperativeHandle } from 'react';
import type React from 'react';
import {
  FileCode,
  Image,
  Box,
  Plus,
  Trash2,
  Upload,
  FolderOpen,
  File,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  Pencil,
  FileInput,
  Scissors,
  Copy,
  Check,
  HardDriveDownload,
  Info,
  Home,
  Wand2,
  Cpu,
  FilePlus2,
  Hash,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { ModelFileType } from '@/lib/supabase/types';
import {
  generateImportStatement,
  generateTextureSvgStatement,
  isTextureSvgFile,
  generateTexturePngStatement,
  isTextureImageFile,
  generateSvgOverlayStatement,
} from '@/utils/importUtils';
import {
  generateOpenScadSnippet,
  getGeometryChildren,
} from '@/utils/svgSplitter';
import { useBlob, type ColoredBlob } from '@/contexts/BlobContext';
import { StlPreviewDialog } from '@/components/files/StlPreviewDialog';
import { JsTexturePreviewDialog } from '@/components/files/JsTexturePreviewDialog';
import { JsShaderPreviewDialog } from '@/components/files/JsShaderPreviewDialog';
import { isShaderScript } from '@/utils/textureUtils';
import { NewFileDialog } from '@/components/files/NewFileDialog';

// File representation for the UI
export interface ProjectFile {
  id: string;
  filename: string;
  fileType: ModelFileType;
  content: string | ArrayBuffer;
  size: number;
  isModified?: boolean;
  isBinary?: boolean;
}

export interface FileManagerActions {
  triggerUpload: () => void;
  triggerCaptureMesh: () => void;
}

interface FileManagerProps {
  files: ProjectFile[];
  activeFileId: string | null;
  onFileSelect: (fileId: string) => void;
  onFileAdd: (file: ProjectFile) => void;
  onFileDelete: (fileId: string) => void;
  onFileRename: (fileId: string, newFilename: string) => void;
  onInsertImport: (importStatement: string) => void;
  onSplitSvg?: (fileId: string) => Promise<string[]>;
  onCaptureMesh?: (files: ProjectFile[]) => void;
  projectTitle?: string;
  missingImports?: string[];
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  className?: string;
  /** Whether the main SCAD file is currently active in the editor */
  isMainFileActive?: boolean;
  /** Called when the user clicks the main file card to return to editing it */
  onReturnToMain?: () => void;
  /** Ref that exposes imperative actions to parent (command palette) */
  actionRef?: React.MutableRefObject<FileManagerActions | null>;
}

/** Convert a project title into a safe filename base (no extension). */
function titleToFilenameBase(title?: string): string {
  if (!title || !title.trim()) return 'mesh';
  return (
    title
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_-]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '') || 'mesh'
  );
}

/** RGBA 0-1 → 6-char hex string, e.g. "ff8800". */
function rgbaToHex([r, g, b]: [number, number, number, number]): string {
  return [r, g, b]
    .map((c) =>
      Math.round(c * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');
}

// Get icon for file type
function getFileIcon(fileType: ModelFileType, className?: string) {
  switch (fileType) {
    case 'scad':
      return <FileCode className={cn('h-4 w-4 text-blue-500', className)} />;
    case 'svg':
      return <Image className={cn('h-4 w-4 text-orange-500', className)} />;
    case 'stl':
      return <Box className={cn('h-4 w-4 text-purple-500', className)} />;
    case 'dxf':
      return <Image className={cn('h-4 w-4 text-green-500', className)} />;
    case 'js':
      return <Wand2 className={cn('h-4 w-4 text-amber-400', className)} />;
    case 'png':
      return <Image className={cn('h-4 w-4 text-pink-500', className)} />;
    default:
      return (
        <File className={cn('h-4 w-4 text-muted-foreground', className)} />
      );
  }
}

// Get file type from extension
export function getFileType(filename: string): ModelFileType {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'scad':
      return 'scad';
    case 'svg':
      return 'svg';
    case 'stl':
      return 'stl';
    case 'dxf':
      return 'dxf';
    case 'dat':
      return 'dat';
    case 'json':
      return 'json';
    case 'js':
      return 'js';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'webp':
      return 'png';
    default:
      return 'txt';
  }
}

// Check if file is binary
export function isBinaryFileType(fileType: ModelFileType): boolean {
  return fileType === 'stl' || fileType === 'png';
}

const EMPTY_MISSING_IMPORTS: string[] = [];

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileManager({
  files,
  activeFileId,
  onFileSelect,
  onFileAdd,
  onFileDelete,
  onFileRename,
  onInsertImport,
  onSplitSvg,
  onCaptureMesh,
  projectTitle,
  missingImports = EMPTY_MISSING_IMPORTS,
  isExpanded = false,
  onExpandedChange,
  className,
  isMainFileActive = true,
  onReturnToMain,
  actionRef,
}: FileManagerProps) {
  const { blob, coloredBlobs } = useBlob();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [fileToRename, setFileToRename] = useState<ProjectFile | null>(null);
  const [newFilename, setNewFilename] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [splitResultOpen, setSplitResultOpen] = useState(false);
  const [splitSnippet, setSplitSnippet] = useState('');
  const [splitFileCount, setSplitFileCount] = useState(0);
  const [splitCopied, setSplitCopied] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);
  // Capture-mesh dialog state
  const [captureDialogOpen, setCaptureDialogOpen] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  // Info / preview dialog state
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoFile, setInfoFile] = useState<ProjectFile | null>(null);
  // STL wireframe preview dialog state
  const [stlPreviewOpen, setStlPreviewOpen] = useState(false);
  const [stlPreviewFile, setStlPreviewFile] = useState<ProjectFile | null>(
    null,
  );
  // JS texture script preview dialog state
  const [jsPreviewOpen, setJsPreviewOpen] = useState(false);
  const [jsPreviewFile, setJsPreviewFile] = useState<ProjectFile | null>(null);
  // JS shader script preview dialog state
  const [jsShaderPreviewOpen, setJsShaderPreviewOpen] = useState(false);
  const [jsShaderPreviewFile, setJsShaderPreviewFile] =
    useState<ProjectFile | null>(null);
  // New-file dialog state
  const [newFileDialogOpen, setNewFileDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeFilter, setActiveFilter] = useState<ModelFileType | null>(null);

  // Expose imperative actions to parent (for command palette)
  useImperativeHandle(actionRef, () => ({
    triggerUpload: () => {
      fileInputRef.current?.click();
    },
    triggerCaptureMesh: () => {
      setCaptureDialogOpen(true);
    },
  }));

  const startInfo = useCallback((file: ProjectFile) => {
    setInfoFile(file);
    setInfoDialogOpen(true);
  }, []);

  // Exportable colored blobs (strip ghosts + excluded)
  const exportableColoredBlobs = coloredBlobs.filter(
    (b) => !b.isGhost && !b.excludedFromExport,
  );
  const isMultiColor = exportableColoredBlobs.length > 1;
  const hasMesh = blob !== null;

  /** Build a single-STL ProjectFile from the main blob. */
  const buildSingleStlFile = useCallback(
    async (sourceBlob: Blob): Promise<ProjectFile> => {
      const base = titleToFilenameBase(projectTitle);
      const arrayBuffer = await sourceBlob.arrayBuffer();
      return {
        id: crypto.randomUUID(),
        filename: `${base}.stl`,
        fileType: 'stl',
        content: arrayBuffer,
        size: arrayBuffer.byteLength,
        isBinary: true,
        isModified: true,
      };
    },
    [projectTitle],
  );

  /** Build one ProjectFile per exportable colored blob. */
  const buildColoredStlFiles = useCallback(
    async (blobs: ColoredBlob[]): Promise<ProjectFile[]> => {
      const base = titleToFilenameBase(projectTitle);
      return Promise.all(
        blobs.map(async (cb, i) => {
          const hex = rgbaToHex(cb.color);
          const arrayBuffer = await cb.blob.arrayBuffer();
          return {
            id: crypto.randomUUID(),
            filename: `${base}_color_${i + 1}_${hex}.stl`,
            fileType: 'stl' as ModelFileType,
            content: arrayBuffer,
            size: arrayBuffer.byteLength,
            isBinary: true,
            isModified: true,
          };
        }),
      );
    },
    [projectTitle],
  );

  const executeSingleCapture = useCallback(async () => {
    if (!blob || !onCaptureMesh) return;
    setIsCapturing(true);
    try {
      const file = await buildSingleStlFile(blob);
      onCaptureMesh([file]);
    } finally {
      setIsCapturing(false);
      setCaptureDialogOpen(false);
    }
  }, [blob, onCaptureMesh, buildSingleStlFile]);

  const executeColoredCapture = useCallback(async () => {
    if (!onCaptureMesh) return;
    setIsCapturing(true);
    try {
      const capturedBlobs =
        exportableColoredBlobs.length > 0 ? exportableColoredBlobs : [];
      const capturedFiles = await buildColoredStlFiles(capturedBlobs);
      onCaptureMesh(capturedFiles);
    } finally {
      setIsCapturing(false);
      setCaptureDialogOpen(false);
    }
  }, [onCaptureMesh, exportableColoredBlobs, buildColoredStlFiles]);

  const handleCaptureMeshClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!blob || !onCaptureMesh) return;
      if (isMultiColor) {
        // Show dialog to let user choose single vs. per-color
        setCaptureDialogOpen(true);
      } else {
        // Single-color: capture immediately
        executeSingleCapture();
      }
    },
    [blob, onCaptureMesh, isMultiColor, executeSingleCapture],
  );

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const uploadedFiles = event.target.files;
      if (!uploadedFiles) return;

      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const fileType = getFileType(file.name);
        const isBinary = isBinaryFileType(fileType);

        let content: string | ArrayBuffer;
        if (isBinary) {
          content = await file.arrayBuffer();
        } else {
          content = await file.text();
        }

        const projectFile: ProjectFile = {
          id: crypto.randomUUID(),
          filename: file.name,
          fileType,
          content,
          size: file.size,
          isBinary,
        };

        onFileAdd(projectFile);
      }

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onFileAdd],
  );

  /** Create a new blank file from a template. */
  const handleNewFileCreate = useCallback(
    (filename: string, content: string, fileType: ModelFileType) => {
      const projectFile: ProjectFile = {
        id: crypto.randomUUID(),
        filename,
        fileType,
        content,
        size: new TextEncoder().encode(content).length,
        isBinary: false,
        isModified: true,
      };
      onFileAdd(projectFile);
    },
    [onFileAdd],
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      const droppedFiles = event.dataTransfer.files;
      if (!droppedFiles) return;

      for (let i = 0; i < droppedFiles.length; i++) {
        const file = droppedFiles[i];
        const fileType = getFileType(file.name);
        const isBinary = isBinaryFileType(fileType);

        let content: string | ArrayBuffer;
        if (isBinary) {
          content = await file.arrayBuffer();
        } else {
          content = await file.text();
        }

        const projectFile: ProjectFile = {
          id: crypto.randomUUID(),
          filename: file.name,
          fileType,
          content,
          size: file.size,
          isBinary,
        };

        onFileAdd(projectFile);
      }
    },
    [onFileAdd],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
    },
    [],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
    },
    [],
  );

  const confirmDelete = useCallback((fileId: string) => {
    setFileToDelete(fileId);
    setDeleteDialogOpen(true);
  }, []);

  const handleDelete = useCallback(() => {
    if (fileToDelete) {
      onFileDelete(fileToDelete);
      setFileToDelete(null);
      setDeleteDialogOpen(false);
    }
  }, [fileToDelete, onFileDelete]);

  const startRename = useCallback((file: ProjectFile) => {
    setFileToRename(file);
    setNewFilename(file.filename);
    setRenameDialogOpen(true);
  }, []);

  const handleRename = useCallback(() => {
    if (fileToRename && newFilename.trim()) {
      onFileRename(fileToRename.id, newFilename.trim());
      setFileToRename(null);
      setNewFilename('');
      setRenameDialogOpen(false);
    }
  }, [fileToRename, newFilename, onFileRename]);

  const handleInsertImport = useCallback(
    (file: ProjectFile) => {
      const statement = generateImportStatement(file.filename);
      onInsertImport(statement);
    },
    [onInsertImport],
  );

  const handleInsertTextureSvg = useCallback(
    (file: ProjectFile) => {
      const statement = generateTextureSvgStatement(file.filename);
      onInsertImport(statement);
    },
    [onInsertImport],
  );

  const handleInsertSvgOverlay = useCallback(
    (file: ProjectFile) => {
      const statement = generateSvgOverlayStatement(file.filename);
      onInsertImport(statement);
    },
    [onInsertImport],
  );

  const handleInsertTexturePng = useCallback(
    (file: ProjectFile) => {
      const statement = generateTexturePngStatement(file.filename);
      onInsertImport(statement);
    },
    [onInsertImport],
  );

  const handleInsertSvgFragment = useCallback(
    (file: ProjectFile, selector: string) => {
      const statement = `import("${file.filename}${selector}");`;
      onInsertImport(statement);
    },
    [onInsertImport],
  );

  /**
   * Build the list of selectable SVG elements for the "Import element"
   * submenu. Each top-level geometry child gets one entry:
   *   - If it has an id attribute → selector "#id", label "#id · <tagname>"
   *   - Otherwise               → selector "[N]", label "[N] · <tagname>"
   * Metadata-only tags (defs, style, etc.) are skipped.
   */
  function buildSvgElements(svgContent: string): SvgElementEntry[] {
    try {
      const children = getGeometryChildren(svgContent);
      return children.map((el, index) => {
        const tag = el.tagName.toLowerCase().replace(/^svg:/, '');
        const id = el.getAttribute('id');
        if (id) {
          return { selector: `#${id}`, label: `#${id} · ${tag}` };
        }
        return { selector: `[${index}]`, label: `[${index}] · ${tag}` };
      });
    } catch {
      return [];
    }
  }

  const handleSplitSvg = useCallback(
    async (file: ProjectFile) => {
      if (!onSplitSvg) return;
      setSplitError(null);
      try {
        const splitFilenames = await onSplitSvg(file.id);
        const snippet = generateOpenScadSnippet(splitFilenames);
        setSplitSnippet(snippet);
        setSplitFileCount(splitFilenames.length);
        setSplitResultOpen(true);
      } catch (err) {
        setSplitError(err instanceof Error ? err.message : String(err));
        setSplitResultOpen(true);
      }
    },
    [onSplitSvg],
  );

  const handleCopySnippet = useCallback(() => {
    navigator.clipboard.writeText(splitSnippet).then(() => {
      setSplitCopied(true);
      setTimeout(() => setSplitCopied(false), 2000);
    });
  }, [splitSnippet]);

  const totalFiles = files.length + missingImports.length;

  // Only show filter types that have at least one file
  const presentTypes = Array.from(
    new Set(files.map((f) => f.fileType)),
  ) as ModelFileType[];
  const filterTypes: ModelFileType[] = (
    [
      'scad',
      'svg',
      'stl',
      'js',
      'png',
      'dxf',
      'dat',
      'json',
      'txt',
    ] as ModelFileType[]
  ).filter((t) => presentTypes.includes(t));

  return (
    <div
      className={cn(
        'flex flex-col border-t border-border bg-background transition-all',
        className,
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Header - always visible */}
      <div
        role="button"
        tabIndex={0}
        className="grid h-9 cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-x-2 border-b border-border px-3 hover:bg-accent/30"
        onClick={() => onExpandedChange?.(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onExpandedChange?.(!isExpanded);
          }
        }}
      >
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium text-foreground">
            Project Files
          </span>
          {totalFiles > 0 && (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {totalFiles}
            </span>
          )}
          {missingImports.length > 0 && (
            <span className="flex items-center gap-1 text-[10px] text-amber-500">
              <AlertCircle className="h-3 w-3" />
              {missingImports.length} missing
            </span>
          )}
        </div>

        {/* Center: collapsed tab strip OR expanded filter pills */}
        {!isExpanded ? (
          /* Collapsed: scrollable filename tab strip */
          <div
            role="presentation"
            className="flex min-w-0 overflow-hidden px-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex h-full items-center gap-0.5 overflow-x-auto"
              style={{ scrollbarWidth: 'none' }}
            >
              {/* Main file tab */}
              {onReturnToMain && (
                <button
                  type="button"
                  className={cn(
                    'flex flex-shrink-0 items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] transition-colors hover:bg-accent/30',
                    isMainFileActive
                      ? 'text-foreground'
                      : 'text-muted-foreground/50',
                  )}
                  onClick={() => {
                    onReturnToMain();
                    onExpandedChange?.(true);
                  }}
                >
                  <Home
                    className={cn(
                      'h-3 w-3 flex-shrink-0',
                      isMainFileActive
                        ? 'text-blue-500'
                        : 'text-muted-foreground/50',
                    )}
                  />
                  <span>main</span>
                </button>
              )}

              {/* Project file tabs */}
              {files.map((file) => {
                const isActive = !isMainFileActive && activeFileId === file.id;
                return (
                  <button
                    key={file.id}
                    type="button"
                    className={cn(
                      'flex flex-shrink-0 items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] transition-colors hover:bg-accent/30',
                      isActive ? 'text-foreground' : 'text-muted-foreground/50',
                    )}
                    onClick={() => {
                      onFileSelect(file.id);
                      onExpandedChange?.(true);
                    }}
                  >
                    {getFileIcon(
                      file.fileType,
                      cn(
                        'h-3 w-3 flex-shrink-0',
                        isActive ? '' : 'text-muted-foreground/50',
                      ),
                    )}
                    <span className="max-w-[80px] truncate">
                      {file.filename}
                    </span>
                  </button>
                );
              })}

              {/* Missing imports — non-interactive, amber */}
              {missingImports.map((filename) => (
                <span
                  key={filename}
                  className="flex flex-shrink-0 items-center gap-1 px-2 py-0.5 text-[10px] text-amber-500/60"
                >
                  <AlertCircle className="h-3 w-3 flex-shrink-0" />
                  <span className="max-w-[80px] truncate">{filename}</span>
                </span>
              ))}
            </div>
          </div>
        ) : filterTypes.length > 1 ? (
          /* Expanded: file type filter pills */
          <div
            role="presentation"
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {filterTypes.map((type) => (
              <button
                key={type}
                onClick={() =>
                  setActiveFilter(activeFilter === type ? null : type)
                }
                className={cn(
                  'rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                  activeFilter === type
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground',
                )}
              >
                .{type}
              </button>
            ))}
          </div>
        ) : (
          <div />
        )}

        <div className="flex items-center justify-end gap-1">
          <DropdownMenu>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Add or create files</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem
                onSelect={() => fileInputRef.current?.click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload file…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setNewFileDialogOpen(true)}
              >
                <FilePlus2 className="mr-2 h-4 w-4" />
                New blank file…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {onCaptureMesh && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={!hasMesh}
                    onClick={handleCaptureMeshClick}
                  >
                    <HardDriveDownload className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {hasMesh ? 'Capture mesh as STL' : 'No mesh compiled yet'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <Button variant="ghost" size="icon" className="h-6 w-6">
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".scad,.svg,.stl,.dxf,.dat,.json,.txt,.js,.png,.jpg,.jpeg,.webp"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Expandable content */}
      {isExpanded && (
        <div
          className={cn(
            'flex-1 overflow-hidden transition-colors',
            isDragging && 'bg-accent/50',
          )}
        >
          {/* Horizontal scrolling file list */}
          <div className="flex h-full min-h-[80px] items-start gap-1 overflow-x-auto p-2">
            {/* Main file card — always first */}
            {onReturnToMain && (
              <button
                type="button"
                className={cn(
                  'flex h-[92px] w-[110px] flex-shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border p-2 transition-colors',
                  isMainFileActive
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50 hover:bg-accent/30',
                )}
                onClick={onReturnToMain}
              >
                <Home className="h-7 w-7 text-blue-500" />
                <span className="line-clamp-2 w-full break-all text-center text-[10px] leading-tight text-foreground">
                  main file
                </span>
              </button>
            )}

            {/* Project files */}
            {files
              .filter(
                (f) => activeFilter === null || f.fileType === activeFilter,
              )
              .map((file) => (
                <FileCard
                  key={file.id}
                  file={file}
                  isActive={!isMainFileActive && activeFileId === file.id}
                  onSelect={() => {
                    if (file.fileType === 'stl') {
                      setStlPreviewFile(file);
                      setStlPreviewOpen(true);
                    } else {
                      onFileSelect(file.id);
                    }
                  }}
                  onDelete={() => confirmDelete(file.id)}
                  onRename={() => startRename(file)}
                  onInsertImport={() => handleInsertImport(file)}
                  onInsertTextureSvg={
                    isTextureSvgFile(file.filename)
                      ? () => handleInsertTextureSvg(file)
                      : undefined
                  }
                  onInsertSvgOverlay={
                    isTextureSvgFile(file.filename)
                      ? () => handleInsertSvgOverlay(file)
                      : undefined
                  }
                  onInsertTexturePng={
                    isTextureImageFile(file.filename)
                      ? () => handleInsertTexturePng(file)
                      : undefined
                  }
                  svgElements={
                    file.fileType === 'svg' &&
                    typeof file.content === 'string'
                      ? buildSvgElements(file.content)
                      : undefined
                  }
                  onInsertSvgSelector={
                    file.fileType === 'svg'
                      ? (selector: string) =>
                          handleInsertSvgFragment(file, selector)
                      : undefined
                  }
                  onSplitSvg={
                    onSplitSvg ? () => handleSplitSvg(file) : undefined
                  }
                  onPreviewTexture={
                    file.fileType === 'js' &&
                    typeof file.content === 'string' &&
                    !isShaderScript(file.content)
                      ? () => {
                          setJsPreviewFile(file);
                          setJsPreviewOpen(true);
                        }
                      : undefined
                  }
                  onPreviewShader={
                    file.fileType === 'js' &&
                    typeof file.content === 'string' &&
                    isShaderScript(file.content)
                      ? () => {
                          setJsShaderPreviewFile(file);
                          setJsShaderPreviewOpen(true);
                        }
                      : undefined
                  }
                  onInfo={() => startInfo(file)}
                />
              ))}

            {/* Missing imports */}
            {missingImports.map((filename) => (
              <div
                key={filename}
                className="flex h-[92px] w-[110px] flex-shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-amber-500/50 bg-amber-500/5 p-2"
              >
                <AlertCircle className="h-5 w-5 text-amber-500" />
                <span className="line-clamp-2 w-full break-all text-center text-[10px] leading-tight text-amber-500">
                  {filename}
                </span>
              </div>
            ))}

            {/* Empty state / drop zone */}
            {files.length === 0 && missingImports.length === 0 && (
              <div
                className={cn(
                  'flex h-[92px] min-w-[200px] flex-1 flex-col items-center justify-center gap-1 rounded-md border border-dashed transition-colors',
                  isDragging ? 'border-primary bg-primary/10' : 'border-border',
                )}
              >
                <Upload className="h-5 w-5 text-muted-foreground/50" />
                <span className="text-[10px] text-muted-foreground">
                  Drop files here or click + to add
                </span>
              </div>
            )}

            {/* Add more card when files exist — dropdown with upload / new */}
            {(files.length > 0 || missingImports.length > 0) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Add file"
                    className={cn(
                      'flex h-[92px] w-[92px] flex-shrink-0 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed transition-colors hover:bg-accent/30',
                      isDragging ? 'border-primary bg-primary/10' : 'border-border',
                    )}
                  >
                    <Plus className="h-5 w-5 text-muted-foreground/50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onSelect={() => fileInputRef.current?.click()}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Upload file…
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => setNewFileDialogOpen(true)}
                  >
                    <FilePlus2 className="mr-2 h-4 w-4" />
                    New blank file…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      )}

      {/* New blank file dialog */}
      <NewFileDialog
        open={newFileDialogOpen}
        onOpenChange={setNewFileDialogOpen}
        onCreateFile={handleNewFileCreate}
        existingFilenames={files.map((f) => f.filename)}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete file?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The file will be permanently removed
              from your project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename file</DialogTitle>
            <DialogDescription>
              Enter a new name for the file. Make sure to keep the file
              extension.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newFilename}
            onChange={(e) => setNewFilename(e.target.value)}
            placeholder="filename.scad"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
            }}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRenameDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleRename}>Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Capture mesh dialog (multi-color: ask single vs. per-color) */}
      <Dialog open={captureDialogOpen} onOpenChange={setCaptureDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Capture mesh as STL</DialogTitle>
            <DialogDescription>
              This model has {exportableColoredBlobs.length} colored parts. How
              would you like to save them?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              className="w-full"
              disabled={isCapturing}
              onClick={executeSingleCapture}
            >
              Save as single STL
            </Button>
            <Button
              variant="outline"
              className="w-full"
              disabled={isCapturing}
              onClick={executeColoredCapture}
            >
              Save one STL per color ({exportableColoredBlobs.length} files)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split SVG result dialog */}
      <Dialog open={splitResultOpen} onOpenChange={setSplitResultOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {splitError ? 'Split SVG failed' : 'SVG split complete'}
            </DialogTitle>
            <DialogDescription>
              {splitError
                ? splitError
                : `${splitFileCount} shape ${splitFileCount === 1 ? 'file' : 'files'} added to your project. Use the snippet below to import them in OpenSCAD.`}
            </DialogDescription>
          </DialogHeader>
          {!splitError && splitSnippet && (
            <div className="relative">
              <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs leading-relaxed text-foreground">
                {splitSnippet}
              </pre>
              <Button
                size="icon"
                variant="ghost"
                className="absolute right-2 top-2 h-6 w-6"
                onClick={handleCopySnippet}
              >
                {splitCopied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          )}
          {!splitError && (
            <p className="text-[10px] text-muted-foreground">
              Note: &lt;defs&gt; (gradients, clip paths) are not copied into
              split files. Shapes referencing shared defs may look different in
              isolation.
            </p>
          )}
          <DialogFooter>
            <Button onClick={() => setSplitResultOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File info / preview dialog */}
      {infoFile && (
        <FileInfoDialog
          file={infoFile}
          open={infoDialogOpen}
          onOpenChange={(open) => {
            setInfoDialogOpen(open);
            if (!open) setInfoFile(null);
          }}
        />
      )}

      {/* STL wireframe preview dialog */}
      <StlPreviewDialog
        file={stlPreviewFile}
        open={stlPreviewOpen}
        onOpenChange={(open) => {
          setStlPreviewOpen(open);
          if (!open) setStlPreviewFile(null);
        }}
      />

      {/* JS texture script preview dialog */}
      <JsTexturePreviewDialog
        file={jsPreviewFile}
        open={jsPreviewOpen}
        onOpenChange={(open) => {
          setJsPreviewOpen(open);
          if (!open) setJsPreviewFile(null);
        }}
      />

      {/* JS shader script preview dialog */}
      <JsShaderPreviewDialog
        file={jsShaderPreviewFile}
        open={jsShaderPreviewOpen}
        onOpenChange={(open) => {
          setJsShaderPreviewOpen(open);
          if (!open) setJsShaderPreviewFile(null);
        }}
      />
    </div>
  );
}

// Individual file card component with context menu
/**
 * One selectable element entry shown in the "Import element" submenu.
 * label    — display string, e.g. "[0] rect" or "#star path"
 * selector — the selector to append to the filename, e.g. "[0]" or "#star"
 */
export interface SvgElementEntry {
  label: string;
  selector: string;
}

interface FileCardProps {
  file: ProjectFile;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onRename: () => void;
  onInsertImport: () => void;
  /** SVG files only: inserts a texture_svg("file.svg") statement */
  onInsertTextureSvg?: () => void;
  /** SVG files only: inserts a svg_overlay() statement */
  onInsertSvgOverlay?: () => void;
  /** PNG/JPG/WebP files only: inserts a texture_png("file.png") statement */
  onInsertTexturePng?: () => void;
  onSplitSvg?: () => void;
  onPreviewTexture?: () => void;
  onPreviewShader?: () => void;
  onInfo: () => void;
  /**
   * SVG files only: ordered list of importable elements.
   * Each entry carries a display label and the selector string to use
   * (either "#id" or "[N]").
   */
  svgElements?: SvgElementEntry[];
  /** SVG files only: called with the selector ("#id" or "[N]") to insert */
  onInsertSvgSelector?: (selector: string) => void;
}

function FileCard({
  file,
  isActive,
  onSelect,
  onDelete,
  onRename,
  onInsertImport,
  onInsertTextureSvg,
  onInsertSvgOverlay,
  onInsertTexturePng,
  onSplitSvg,
  onPreviewTexture,
  onPreviewShader,
  onInfo,
  svgElements,
  onInsertSvgSelector,
}: FileCardProps) {
  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <button
          type="button"
          className={cn(
            'group flex h-[92px] w-[110px] flex-shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border p-2 transition-colors',
            isActive
              ? 'border-primary bg-primary/10'
              : 'border-border hover:border-primary/50 hover:bg-accent/30',
          )}
          onClick={onSelect}
        >
          <div className="relative">
            {getFileIcon(file.fileType, 'h-7 w-7')}
            {file.isModified && (
              <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-blue-500" />
            )}
          </div>
          <span className="line-clamp-2 w-full break-all text-center text-[10px] leading-tight text-foreground">
            {file.filename}
          </span>
          <span className="text-[9px] text-muted-foreground">
            {formatFileSize(file.size)}
          </span>
        </button>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        <ContextMenuItem onClick={onInfo}>
          <Info className="mr-2 h-4 w-4" />
          File info
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onInsertImport}>
          <FileInput className="mr-2 h-4 w-4" />
          Insert import
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => {
            const statement = generateImportStatement(file.filename);
            navigator.clipboard.writeText(statement);
          }}
        >
          Copy import statement
        </ContextMenuItem>
        {file.fileType === 'svg' && onInsertSvgSelector && (
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <Hash className="mr-2 h-4 w-4" />
              Import element
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="max-h-64 w-56 overflow-y-auto">
              {svgElements && svgElements.length > 0 ? (
                svgElements.map((entry) => (
                  <ContextMenuItem
                    key={entry.selector}
                    onClick={() => onInsertSvgSelector(entry.selector)}
                  >
                    <span className="truncate font-mono text-xs">
                      {entry.label}
                    </span>
                  </ContextMenuItem>
                ))
              ) : (
                <ContextMenuItem disabled>
                  <span className="text-xs text-muted-foreground">
                    No elements found
                  </span>
                </ContextMenuItem>
              )}
            </ContextMenuSubContent>
          </ContextMenuSub>
        )}
        {file.fileType === 'svg' && onInsertTextureSvg && (
          <>
            <ContextMenuItem onClick={onInsertTextureSvg}>
              <Image className="mr-2 h-4 w-4" />
              Insert as texture_svg()
            </ContextMenuItem>
          </>
        )}
        {file.fileType === 'svg' && onInsertSvgOverlay && (
          <>
            <ContextMenuItem onClick={onInsertSvgOverlay}>
              <Image className="mr-2 h-4 w-4" />
              Insert as svg_overlay()
            </ContextMenuItem>
          </>
        )}
        {file.fileType === 'png' && onInsertTexturePng && (
          <>
            <ContextMenuItem onClick={onInsertTexturePng}>
              <Image className="mr-2 h-4 w-4" />
              Insert as texture_png()
            </ContextMenuItem>
          </>
        )}
        {file.fileType === 'svg' && onSplitSvg && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onSplitSvg}>
              <Scissors className="mr-2 h-4 w-4" />
              Split into shapes
            </ContextMenuItem>
          </>
        )}
        {file.fileType === 'js' && onPreviewTexture && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onPreviewTexture}>
              <Wand2 className="mr-2 h-4 w-4" />
              Preview texture
            </ContextMenuItem>
          </>
        )}
        {file.fileType === 'js' && onPreviewShader && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onPreviewShader}>
              <Cpu className="mr-2 h-4 w-4" />
              Preview shader
            </ContextMenuItem>
          </>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onRename}>
          <Pencil className="mr-2 h-4 w-4" />
          Rename
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

// ─── File info / preview dialog ───────────────────────────────────────────────

interface FileInfoDialogProps {
  file: ProjectFile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Label row used in the info dialog. */
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="w-20 flex-shrink-0 text-muted-foreground">{label}</span>
      <span className="break-all text-foreground">{value}</span>
    </div>
  );
}

/** Derive a human-readable type label. */
function fileTypeLabel(fileType: ModelFileType): string {
  switch (fileType) {
    case 'scad':
      return 'OpenSCAD script';
    case 'svg':
      return 'SVG image';
    case 'stl':
      return 'STL mesh (binary)';
    case 'dxf':
      return 'DXF drawing';
    case 'json':
      return 'JSON data';
    case 'dat':
      return 'Data file';
    case 'txt':
      return 'Text file';
    case 'js':
      return 'JS texture script';
    case 'png':
      return 'Raster image (PNG/JPG/WebP)';
    default:
      return fileType;
  }
}

function FileInfoDialog({ file, open, onOpenChange }: FileInfoDialogProps) {
  const isText = typeof file.content === 'string';
  const textContent = isText ? (file.content as string) : null;

  // Count lines for text files
  const lineCount = textContent ? textContent.split('\n').length : null;

  // For SVG: render inline
  const isSvg = file.fileType === 'svg' && isText;

  // For text-based files (non-SVG): show a code preview
  const showTextPreview =
    isText &&
    !isSvg &&
    ['scad', 'json', 'txt', 'dat', 'dxf', 'js'].includes(file.fileType);

  // Truncate preview to first 80 lines
  const previewLines = textContent
    ? textContent.split('\n').slice(0, 80).join('\n')
    : null;
  const isTruncated = lineCount !== null && lineCount > 80;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-xl flex-col gap-4 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getFileIcon(file.fileType)}
            <span className="break-all">{file.filename}</span>
            {file.isModified && (
              <span className="ml-1 rounded-full bg-blue-500/20 px-1.5 py-0.5 text-[10px] font-normal text-blue-400">
                modified
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Metadata */}
        <div className="flex flex-col gap-1.5 rounded-md bg-muted/50 px-3 py-2.5">
          <InfoRow label="Type" value={fileTypeLabel(file.fileType)} />
          <InfoRow label="Size" value={formatFileSize(file.size)} />
          {lineCount !== null && (
            <InfoRow label="Lines" value={`${lineCount.toLocaleString()}`} />
          )}
        </div>

        {/* Preview area */}
        {isSvg && textContent && (
          <div className="flex min-h-0 flex-1 flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">Preview</span>
            <div
              className="flex flex-1 items-center justify-center overflow-auto rounded-md border border-border bg-white p-4"
              style={{ minHeight: '160px', maxHeight: '320px' }}
              dangerouslySetInnerHTML={{ __html: textContent }}
            />
          </div>
        )}

        {showTextPreview && previewLines !== null && (
          <div className="flex min-h-0 flex-1 flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">
              Preview
              {isTruncated && (
                <span className="ml-1 text-muted-foreground/60">
                  (first 80 of {lineCount?.toLocaleString()} lines)
                </span>
              )}
            </span>
            <pre className="min-h-0 flex-1 overflow-auto rounded-md bg-muted p-3 text-[11px] leading-relaxed text-foreground">
              {previewLines}
            </pre>
          </div>
        )}

        {!isSvg && !showTextPreview && (
          <div className="flex items-center justify-center rounded-md border border-border bg-muted/30 py-8 text-xs text-muted-foreground">
            Binary file — no text preview available
          </div>
        )}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
